import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { formatDateDMY } from '../utils/dateUtils';
import { getVacationRequests, updateVacationStatus } from '../services/vacationRequestService';
import { fetchAllUsers } from '../slices/authSlice';
import { AppDispatch, RootState } from '../store/store';

interface VacationRequest {
  _id: string;
  userId: { _id: string; username: string };
  startDate: string;
  endDate: string;
  status: 'pending' | 'approved' | 'declined';
  comment?: string;
}

const VacationAdminPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const allUsers = useSelector((state: RootState) => state.auth.allUsers || []);
  const [requests, setRequests] = useState<VacationRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState('');
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');
  const [showReviewedRequests, setShowReviewedRequests] = useState(true);
  const pageSize = 10;
  const navigate = useNavigate();

  const fetchRequests = async (token: string) => {
    try {
      const data = await getVacationRequests(token);
      setRequests(data);
      setCurrentPage(1);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Unbekannter Fehler');
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Nicht eingeloggt. Bitte zuerst einloggen.');
      navigate('/login');
      return;
    }

    fetchRequests(token);
    dispatch(fetchAllUsers());
  }, [dispatch, navigate]);

  const handleAction = async (id: string, action: 'approve' | 'decline') => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      await updateVacationStatus(id, action, token);
      fetchRequests(token); // Reload after action
    } catch (err: any) {
      setError(err.message || 'Unbekannter Fehler bei der Aktion');
    }
  };

  const availableYears = useMemo(() => {
    const years = new Set<number>([currentYear, currentYear + 1]);

    requests.forEach((request) => {
      const startYear = new Date(request.startDate).getFullYear();
      const endYear = new Date(request.endDate).getFullYear();

      if (!Number.isNaN(startYear)) years.add(startYear);
      if (!Number.isNaN(endYear)) years.add(endYear);
    });

    return Array.from(years).sort((a, b) => b - a);
  }, [currentYear, requests]);

  const filteredRequests = useMemo(() => {
    return requests
      .filter((request) => {
        const startYear = new Date(request.startDate).getFullYear();
        const endYear = new Date(request.endDate).getFullYear();
        const matchesUser = !selectedUserId || request.userId?._id === selectedUserId;
        const matchesYear =
          selectedYear === 'all' || startYear === selectedYear || endYear === selectedYear;
        const matchesReviewStatus = showReviewedRequests || request.status === 'pending';

        return matchesUser && matchesYear && matchesReviewStatus;
      })
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  }, [requests, selectedUserId, selectedYear, showReviewedRequests]);

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / pageSize));
  const pageStartIndex = (currentPage - 1) * pageSize;
  const visibleRequests = filteredRequests.slice(pageStartIndex, pageStartIndex + pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedUserId, selectedYear, showReviewedRequests]);

  const goToPage = (page: number) => {
    setCurrentPage(Math.min(totalPages, Math.max(1, page)));
  };

  const paginationItems = Array.from({ length: totalPages }, (_, index) => index + 1)
    .filter(
      (page) =>
        page === 1 ||
        page === totalPages ||
        Math.abs(page - currentPage) <= 1
    )
    .reduce<Array<number | 'ellipsis'>>((items, page) => {
      const previous = items[items.length - 1];
      if (typeof previous === 'number' && page - previous > 1) {
        items.push('ellipsis');
      }
      items.push(page);
      return items;
    }, []);

  return (
    <div className="container">
      <h2>Urlaubsanträge verwalten</h2>
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="d-flex gap-3 flex-wrap mt-3">
        <div style={{ minWidth: 260 }}>
          <label className="form-label">Nutzer</label>
          <select
            className="form-select"
            value={selectedUserId}
            onChange={(event) => setSelectedUserId(event.target.value)}
          >
            <option value="">Gesamt</option>
            {allUsers.map((user) => (
              <option key={user._id} value={user._id}>
                {user.username}
              </option>
            ))}
          </select>
        </div>
        <div style={{ width: 140 }}>
          <label className="form-label">Jahr</label>
          <select
            className="form-select"
            value={selectedYear}
            onChange={(event) =>
              setSelectedYear(event.target.value === 'all' ? 'all' : Number(event.target.value))
            }
          >
            <option value="all">Gesamt</option>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <label className="form-check report-export-option align-self-end mb-2" htmlFor="vacation-admin-show-approved">
          <input
            className="form-check-input report-export-checkbox"
            type="checkbox"
            id="vacation-admin-show-approved"
            checked={showReviewedRequests}
            onChange={(event) => setShowReviewedRequests(event.target.checked)}
          />
          <span className="form-check-label">
            Genehmigte und abgelehnte Anträge anzeigen
          </span>
        </label>
      </div>

      <table className="table table-bordered mt-3">
        <thead>
          <tr>
            <th>Nutzer</th>
            <th>Zeitraum</th>
            <th>Kommentar</th>
            <th>Status</th>
            <th>Aktion</th>
          </tr>
        </thead>
        <tbody>
          {visibleRequests.map((req) => (
            <tr key={req._id}>
              <td>{req.userId?.username}</td>
              <td>
                {formatDateDMY(req.startDate)} – {formatDateDMY(req.endDate)}
              </td>
              <td>{req.comment || '–'}</td>
              <td>
                {req.status === 'pending' && <span className="text-warning">Offen</span>}
                {req.status === 'approved' && <span className="text-success">Genehmigt</span>}
                {req.status === 'declined' && <span className="text-danger">Abgelehnt</span>}
              </td>
              <td>
                {req.status === 'pending' ? (
                  <>
                    <button className="btn btn-success btn-sm me-2" onClick={() => handleAction(req._id, 'approve')}>
                      Genehmigen
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleAction(req._id, 'decline')}>
                      Ablehnen
                    </button>
                  </>
                ) : (
                  '–'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {filteredRequests.length > pageSize && (
        <nav aria-label="Urlaubsverwaltung Seiten" className="app-pagination">
          <ul className="pagination pagination-sm app-pagination-list">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" type="button" onClick={() => goToPage(currentPage - 1)}>
                ‹
              </button>
            </li>

            {paginationItems.map((item, index) =>
              item === 'ellipsis' ? (
                <li key={`ellipsis-${index}`} className="page-item disabled">
                  <span className="page-link">…</span>
                </li>
              ) : (
                <li key={item} className={`page-item ${item === currentPage ? 'active' : ''}`}>
                  <button className="page-link" type="button" onClick={() => goToPage(item)}>
                    {item}
                  </button>
                </li>
              )
            )}

            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" type="button" onClick={() => goToPage(currentPage + 1)}>
                ›
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default VacationAdminPage;
